import React, { useEffect, useState } from "react";
import {
  Tab,
  TabList,
  Button,
  Spinner,

} from "@fluentui/react-components";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import { Add20Filled, Edit20Filled, Dismiss20Regular, Save20Regular, Edit20Regular } from "@fluentui/react-icons";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  GetQueue,
  AddQueue,
  UpdateQueue,
  getAllQueue,
} from "../../Services/HeedifyServices";
import { getAccessToken } from "../../Services/GraphServices";
import "../CSS/AgentsComponent.css";
import QueueForm from "./QueueForm";
import QueueServiceIntegration from "./queueServiceIntegration";
import { emptyQueueObject } from "../GenericComponent/GenericData/emptyQueueObject";
import QueueAgentList from "./QueueAgentList";
import QueueFallback from "./QueueFallback";
import QueueVIP from "./QueueVIP";
import QueueSLA from "./QueueSLA";
import QueueCallback from "./QueueCallback";
import QueueClassification from "./QueueClassification";
import ChannelsContainer from "../../Containers/Queues/ChannelsContainer";
//import AgentQueues from "./AgentQueues";

import { useAbility } from "../../Contexts/AbilityContext";
import { useNotification } from '../GenericComponent/NotificationSystem';
export default function QueuesComponent(props) {
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const userId = activeAccount?.localAccountId;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { queueId, scopeParam } = useParams();
  const isCreateMode = queueId === "newQueue";
  const isEditMode = !isCreateMode;
  const [queue, setQueue] = useState(emptyQueueObject);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState("configuration");
  const [formValid, setFormValid] = useState(true);
  const [scopes, setScopes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // 🔐 Ability pour permissions
  const ability = useAbility();
  const { showSuccess, showError } = useNotification();

  const location = useLocation();
  // URL d'abord (survit au rechargement), puis `location.state` (liens déjà émis), puis
  // "default" en dernier recours.
  const queueScope = scopeParam ? decodeURIComponent(scopeParam) : (location.state?.scope || "default");

  // ⚡ Calculer et ajouter permissions dans queue à chaque changement de scope
  useEffect(() => {
    if (!ability || !queue?.scope) return;
    
    const scope = queue.scope;
    const calculatedPermissions = {
  
      configuration: {
        canCreate: ability.can("create", `Queue.Configuration@${scope}`),
        canRead: ability.can("read", `Queue.Configuration@${scope}`),
        canUpdate: ability.can("update", `Queue.Configuration@${scope}`),
        canDelete: ability.can("delete", `Queue.Configuration@${scope}`),
      },
      agentList: {
        canCreate: ability.can("create", `Queue.AgentList@${scope}`),
        canRead: ability.can("read", `Queue.AgentList@${scope}`),
        canUpdate: ability.can("update", `Queue.AgentList@${scope}`),
        canDelete: ability.can("delete", `Queue.AgentList@${scope}`),
      },
      channels: {
        canCreate: ability.can("create", `Queue.ChannelsAssignement@${scope}`),
        canRead: ability.can("read", `Queue.ChannelsAssignement@${scope}`),
        canUpdate: ability.can("update", `Queue.ChannelsAssignement@${scope}`),
        canDelete: ability.can("delete", `Queue.ChannelsAssignement@${scope}`),
      },
      ServiceIntegration: {
        canCreate: ability.can("create", `Queue.ServiceIntegration@${scope}`),
        canRead: ability.can("read", `Queue.ServiceIntegration@${scope}`),
        canUpdate: ability.can("update", `Queue.ServiceIntegration@${scope}`),
        canDelete: ability.can("delete", `Queue.ServiceIntegration@${scope}`),
      },
    };
    
    setQueue(prev => ({ ...prev, permissions: calculatedPermissions }));
  }, [ability, queue?.scope, setQueue]);
  const fetchQueue = (queueId) => {
    getAccessToken(instance, activeAccount)
      .then((token) => {
        GetQueue(props.ACTIVE_TENANT, userId, queueId, queueScope, token).then(
          (result) => {
            // GetQueue rend null sur erreur (403, 404, réseau — le service les avale toutes).
            //
            // Le simple `return` laissait l'écran sur l'objet vide initial : l'utilisateur voyait
            // un FORMULAIRE DE CRÉATION vierge, « This field is required » compris, sur une file
            // qu'il venait d'ouvrir. Rien ne distinguait « pas de droits », « introuvable » et
            // « panne réseau ». Constaté le 17/08/2026 sur une file d'un AUTRE tenant, restée
            // affichée dans la liste après un changement de client.
            if (!result) {
              console.error("Impossible de charger la queue (accès refusé ou introuvable) :", queueId);
              setLoadError(true);
              return;
            }
            setIsEditing(true);
            setQueue(result);
          }
        );
      })
      .catch((err) => {
        console.error("Erreur get queue :", err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Normalize queue data to match old app format
  const normalizeQueueData = (queueData) => {
    const normalized = { ...queueData };
    
    // Convert string numbers to actual numbers (these should be numbers)
    if (normalized.maxAlertTime) normalized.maxAlertTime = Number(normalized.maxAlertTime);
    if (normalized.wrapUpTime) normalized.wrapUpTime = Number(normalized.wrapUpTime);
    if (normalized.sla) normalized.sla = Number(normalized.sla);
    if (normalized.slaThreshold) normalized.slaThreshold = Number(normalized.slaThreshold);
    
    // Keep these as strings (old app format)
    if (normalized.maxInQueue) normalized.maxInQueue = String(normalized.maxInQueue);
    if (normalized.maxWaitTime) normalized.maxWaitTime = String(normalized.maxWaitTime);
    
    // Ensure waitTimeIntervals are all strings
    if (normalized.waitTimeIntervals && Array.isArray(normalized.waitTimeIntervals)) {
      normalized.waitTimeIntervals = normalized.waitTimeIntervals.map(val => String(val));
    }
    
    // Fix text2speech casing (should be lowercase)
    if (normalized.queueFallBackMessage?.type === "Text2speech") {
      normalized.queueFallBackMessage.type = "text2speech";
    }
    
    // Ensure integrationParams has all required fields
    if (normalized.integrationParams) {
      normalized.integrationParams = {
        servicetype: normalized.integrationParams?.servicetype || "none",
        serviceURL: normalized.integrationParams?.serviceURL || "",
        serviceApiKey: normalized.integrationParams?.serviceApiKey || "",
        CRMName: normalized.integrationParams?.CRMName || "",
        resultServiceURL: normalized.integrationParams?.resultServiceURL || "",
        serviceParameters: normalized.integrationParams?.serviceParameters || [],
        method: normalized.integrationParams?.method || "POST",
        headers: normalized.integrationParams?.headers || {}
      };
    }
    
    // Ensure keepAgentOnRequeue exists and is boolean
    if (normalized.keepAgentOnRequeue === undefined || normalized.keepAgentOnRequeue === null) {
      normalized.keepAgentOnRequeue = false;
    }
    
    // Ensure closeCRMPopUpOn exists with default value
    if (!normalized.closeCRMPopUpOn) {
      normalized.closeCRMPopUpOn = "CALL_RING_END";
    }
    
    return normalized;
  };

  const createQueue = () => {
    setSaving(true);
    return getAccessToken(instance, activeAccount)
      .then((token) => {
        // Supprimer permissions avant envoi
        const { permissions: _, ...queueData } = queue;
        // Normalize data format
        const normalizedData = normalizeQueueData(queueData);
        return AddQueue(props.ACTIVE_TENANT, userId, queue.scope, normalizedData, token).then(
          (result) => {
            if (result?.data?.success === true) {
              return token; // Return token for next step
            } else {
              const errorMsg = result?.data?.message || "erreur inconnue";
              console.error("Queue non créée:", result);
              showError(t("QUEUE_CREATION_FAILED"), errorMsg);
              throw new Error(errorMsg);
            }
          }
        );
      })
      .then((token) => {
        // Refresh queues from all scopes
        const scopeNames = props.SCOPES_NAMES || [];
        const queuesPromises = scopeNames.map((s) =>
          getAllQueue(props.ACTIVE_TENANT, userId, s, token)
        );
        return Promise.all(queuesPromises);
      })
      .then((results) => {
        // Merge queues objects (same format as InitComponent)
        const mergedQueues = results
          .filter(item => item && typeof item === "object" && !Array.isArray(item) && Object.keys(item).length > 0)
          .reduce((acc, curr) => ({ ...acc, ...curr }), {});
        props.setQueues(mergedQueues);
        showSuccess(t("QUEUE_CREATED_SUCCESS"));
        navigate("/queues");
      })
      .catch((err) => {
        console.error("Erreur ajout queue :", err);
        showError(t("QUEUE_CREATION_ERROR"), err?.message);
      })
      .finally(() => {
        setSaving(false);
      });
  };
  const updateQueue = () => {
    let updatedQueue = null;
    setSaving(true);
    return getAccessToken(instance, activeAccount)
      .then((token) => {
        // Supprimer permissions avant envoi
        const { permissions: _, ...queueData } = queue;
        // Normalize data format
        const normalizedData = normalizeQueueData(queueData);
        return UpdateQueue(
          props.ACTIVE_TENANT,
          userId,
          queueId,
          queue.scope,
          normalizedData,
          token
        ).then((result) => {
          if (result?.data?.message === "Updated" && result?.data?.success === true) {
            updatedQueue = result.data.queue; // Sauvegarder la queue mise à jour
            return token; // Return token for next step
          } else {
            const errorMsg = result?.data?.message || "erreur inconnue";
            console.error("Queue non mise à jour:", result);
            showError(t("QUEUE_UPDATE_FAILED"), errorMsg);
            throw new Error(errorMsg);
          }
        });
      })
      .then((token) => {
        // Refresh queues from all scopes
        const scopeNames = props.SCOPES_NAMES || [];
        const queuesPromises = scopeNames.map((s) =>
          getAllQueue(props.ACTIVE_TENANT, userId, s, token)
        );
        return Promise.all(queuesPromises);
      })
      .then((results) => {
        // Merge queues objects (same format as InitComponent)
        const mergedQueues = results
          .filter(item => item && typeof item === "object" && !Array.isArray(item) && Object.keys(item).length > 0)
          .reduce((acc, curr) => ({ ...acc, ...curr }), {});
        
        // Forcer l'inclusion de la queue mise à jour
        if (updatedQueue) {
          mergedQueues[updatedQueue?.RowKey] = updatedQueue;
        }
        
        props.setQueues(mergedQueues);
        showSuccess(t("QUEUE_UPDATED_SUCCESS"));
        // update : on RESTE sur la même URL (pas de navigate).
      })
      .catch((err) => {
        console.error("Erreur edit queue :", err);
        showError(t("QUEUE_UPDATE_ERROR"), err?.message);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const handleSubmit = () => {
    if (isEditing) updateQueue();
    else createQueue();
  };
  useEffect(() => {
    if (props.SCOPES_NAMES?.length) {
      setScopes(props.SCOPES_NAMES);
    }
    if (queueId && queueId !== "newQueue") {
      // mode édition
      fetchQueue(queueId);
    } else {
      // mode création - défaut = premier scope où l'utilisateur peut réellement créer
      // une queue (au lieu de "default", que l'user non-default ne voit pas / ne peut
      // pas créer). On retombe sur le 1er scope connu, puis "default" en dernier recours.
      const scopeNames = props.SCOPES_NAMES || [];
      const defaultScope =
        scopeNames.find(s => ability?.can("create", `Queue.main@${s}`)) ||
        scopeNames[0] ||
        "default";
      setQueue(prev => ({ ...prev, scope: defaultScope }));
      setLoading(false);
    }
  }, [queueId, props.SCOPES_NAMES, ability]);

 
  return (
    <>
      <div className="queues-page-container">
        {loading ? (
          <Spinner />
        ) : loadError ? (
          <div className="queue-load-error" style={{ padding: "2rem", textAlign: "center" }}>
            <h3>{t("QUEUE_LOAD_FAILED")}</h3>
            <p style={{ opacity: 0.7 }}>{queueId}</p>
            <Button appearance="primary" onClick={() => navigate("/queues")}>
              {t("BACK_TO_LIST")}
            </Button>
          </div>
        ) : (
          <>
            <div className="div-wrapper">
              <TabList
                selectedValue={selectedTab}
                onTabSelect={(_, data) => setSelectedTab(data.value)}
                className="tab-list"
              >
                {queue.permissions?.configuration?.canRead && (
                  <Tab
                    className={`tab  ${selectedTab === "configuration" ? "selected" : ""}`} 
                    value="configuration"
                  >
                    {t("QUEUE_CONFIGURATION")}
                  </Tab>
                )}
                {queue.permissions?.agentList?.canRead && (
                  <Tab
                    className={`tab  ${selectedTab === "agent-list" ? "selected" : ""}`}
                    value="agent-list"
                  >
                    {t("AGENT_CONFIGURATION")}
                  </Tab>
                )}
                {queue.permissions?.configuration?.canRead && (
                  <Tab
                    className={`tab  ${selectedTab === "fallback" ? "selected" : ""}`}
                    value="fallback"
                  >
                    {t("FALLBACK")}
                  </Tab>
                )}
                {queue.permissions?.configuration?.canRead && (
                  <Tab
                    className={`tab  ${selectedTab === "vip" ? "selected" : ""}`}
                    value="vip"
                  >
                    {t("VIP")}
                  </Tab>
                )}
                {queue.permissions?.configuration?.canRead && (
                  <Tab
                    className={`tab  ${selectedTab === "sla" ? "selected" : ""}`}
                    value="sla"
                  >
                    {t("SLA")}
                  </Tab>
                )}
                {queue.permissions?.configuration?.canRead && (
                  <Tab
                    className={`tab  ${selectedTab === "callback" ? "selected" : ""}`}
                    value="callback"
                  >
                    {t("CALLBACK")}
                  </Tab>
                )}
                {/* La classification est un réglage de file au même titre que le rappel : elle
                    décide de ce que l'agent voit à la fin de CHAQUE appel de cette file. */}
                {queue.permissions?.configuration?.canRead && (
                  <Tab
                    className={`tab  ${selectedTab === "classification" ? "selected" : ""}`}
                    value="classification"
                  >
                    {t("CLASSIFICATION")}
                  </Tab>
                )}
                 {queue.permissions?.ServiceIntegration?.canRead && (
                <Tab
                  className={`tab  ${selectedTab === "service-integration" ? "selected" : ""}`}
                  value="service-integration"
                >
                  {t("SERVICE_INTEGRATION")}
                </Tab>
                 )}
                {queue?.channels?.chat && queue.permissions?.channels?.canRead && (
                  <Tab
                    className={`tab  ${selectedTab === "channel" ? "selected" : ""}`}
                    value="channel"
                  >
                    {t("CHANNEL")}
                  </Tab>
                )}
                
              </TabList >
              <div style={{ display: 'flex', gap: '10px' }}>
                <Button
                  icon={<Dismiss20Regular />}
                  appearance="secondary"
                  onClick={() => navigate("/queues")}
                >
                  {t("CANCEL")}
                </Button>
                <Button
                  icon={saving ? <Spinner size="tiny" /> : (isEditMode ? <Edit20Regular /> : <Save20Regular />)}
                  className="btn-add-style"
                  disabled={saving || !formValid || (isEditMode ? !queue.permissions?.configuration?.canUpdate : !queue.permissions?.configuration?.canCreate)}
                  appearance="primary"
                  onClick={() => handleSubmit()}
                >
                  {t("SAVE")}
                </Button>
              </div>
            </div >
            <div className="queues-content">
              {selectedTab === "configuration" && (
                <QueueForm
                  queues={props.QUEUES}
                  setFormValid={setFormValid}
                  isUpdate={isEditMode}
                  queueId={isEditMode ? queueId : "newQueue"}
                  queue={queue}
                  setQueue={setQueue}
                  scopes={scopes}
                  medias={props.MEDIAS}
                  activeTenant={props.ACTIVE_TENANT}
                  permissions={queue.permissions?.configuration}
                />
              )}
              {selectedTab === "agent-list" && (
                <QueueAgentList
                  agentId={isEditMode ? queueId : "newQueue"}
                  queue={queue}
                  setQueue={setQueue}
                  agents={props.AGENTS}
                  permissions={queue.permissions?.agentList}
                  isUpdate={isEditMode}
                />
              )}
              {selectedTab === "fallback" && (
                <QueueFallback
                  queue={queue}
                  setQueue={setQueue}
                  medias={props.MEDIAS}
                  activeTenant={props.ACTIVE_TENANT}
                  permissions={queue.permissions?.configuration}
                  isUpdate={isEditMode}
                />
              )}
              {selectedTab === "vip" && (
                <QueueVIP
                  queue={queue}
                  setQueue={setQueue}
                  permissions={queue.permissions?.configuration}
                  isUpdate={isEditMode}
                />
              )}
              {selectedTab === "sla" && (
                <QueueSLA
                
                  queue={queue}
                  setQueue={setQueue}
                  permissions={queue.permissions?.configuration}
                  isUpdate={isEditMode}
                />
              )}
              {selectedTab === "callback" && (
                <QueueCallback
                  queue={queue}
                  setQueue={setQueue}
                  permissions={queue.permissions?.configuration}
                  isUpdate={isEditMode}
                />
              )}
              {selectedTab === "classification" && (
                <QueueClassification
                  queue={queue}
                  setQueue={setQueue}
                  permissions={queue.permissions?.configuration}
                  isUpdate={isEditMode}
                />
              )}
              {selectedTab === "service-integration" && (
                <QueueServiceIntegration
                  setFormValid={setFormValid}
                  isUpdate={isEditMode}
                  agentId={isEditMode ? queueId : "newQueue"}
                  queue={queue}
                  setQueue={setQueue}
                  agents={props.AGENTS}
                  permissions={queue.permissions?.ServiceIntegration}
                />
              )}
              {selectedTab === "channel" && (
                <ChannelsContainer queue={queue} permissions={queue.permissions?.channels} />
              )}
            </div>
          </>)}
      </div>
    </>
  );
}